import { graphQlClient } from '@config/graphqlClient';
import GeofenceGraphQL from '@graphql/geofence.queries';
import {
  Geofence,
  GeofenceCategory,
  GeofenceCategoryCreateInput,
  GeofenceCategoryUpdateInput,
  GeofenceCreateInput,
  GeofenceFilterInput,
  GeofenceUpdateInput,
} from '@models/geofence.model';

// ==================== RESPONSE TYPES ====================

interface GetGeofenceCategoriesResponse {
  getGeofenceCategories: GeofenceCategory[];
}
interface GetActiveGeofenceCategoriesResponse {
  getActiveGeofenceCategories: GeofenceCategory[];
}
interface GetGlobalGeofenceCategoriesResponse {
  getGlobalGeofenceCategories: GeofenceCategory[];
}
interface GetGeofenceCategoryResponse {
  getGeofenceCategory: GeofenceCategory;
}
interface CreateGeofenceCategoryResponse {
  createGeofenceCategory: GeofenceCategory;
}
interface UpdateGeofenceCategoryResponse {
  updateGeofenceCategory: GeofenceCategory;
}
interface UpdateGeofenceCategorySortOrderResponse {
  updateGeofenceCategorySortOrder: GeofenceCategory[];
}

interface GetGeofencesResponse {
  getGeofences: Geofence[];
}
interface GetGeofenceResponse {
  getGeofence: Geofence;
}
interface CreateGeofenceResponse {
  createGeofence: Geofence;
}
interface UpdateGeofenceResponse {
  updateGeofence: Geofence;
}
interface SetGeofenceActiveResponse {
  setGeofenceActive: Geofence;
}
interface DeleteResponse {
  [key: string]: { affected: number };
}

// ==================== SERVICE ====================

export const geofenceService = {
  // ===== Categories =====

  async getGeofenceCategories(): Promise<GeofenceCategory[]> {
    const data: GetGeofenceCategoriesResponse = await graphQlClient.request(
      GeofenceGraphQL.queries.getGeofenceCategories,
    );
    return data.getGeofenceCategories || [];
  },

  async getActiveGeofenceCategories(): Promise<GeofenceCategory[]> {
    const data: GetActiveGeofenceCategoriesResponse = await graphQlClient.request(
      GeofenceGraphQL.queries.getActiveGeofenceCategories,
    );
    return data.getActiveGeofenceCategories || [];
  },

  async getGlobalGeofenceCategories(): Promise<GeofenceCategory[]> {
    const data: GetGlobalGeofenceCategoriesResponse = await graphQlClient.request(
      GeofenceGraphQL.queries.getGlobalGeofenceCategories,
    );
    return data.getGlobalGeofenceCategories || [];
  },

  async getGeofenceCategory(geofenceCategoryId: string): Promise<GeofenceCategory> {
    if (!geofenceCategoryId) throw new Error('geofenceCategoryId is required');
    const data: GetGeofenceCategoryResponse = await graphQlClient.request(
      GeofenceGraphQL.queries.getGeofenceCategory,
      { geofenceCategoryId },
    );
    return data.getGeofenceCategory;
  },

  async createGeofenceCategory(
    input: GeofenceCategoryCreateInput,
  ): Promise<GeofenceCategory> {
    const data: CreateGeofenceCategoryResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.createGeofenceCategory,
      { category: input },
    );
    return data.createGeofenceCategory;
  },

  async updateGeofenceCategory(
    input: GeofenceCategoryUpdateInput,
  ): Promise<GeofenceCategory> {
    const data: UpdateGeofenceCategoryResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.updateGeofenceCategory,
      { category: input },
    );
    return data.updateGeofenceCategory;
  },

  async updateGeofenceCategorySortOrder(
    categoryIds: string[],
  ): Promise<GeofenceCategory[]> {
    const data: UpdateGeofenceCategorySortOrderResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.updateGeofenceCategorySortOrder,
      { categoryIds },
    );
    return data.updateGeofenceCategorySortOrder || [];
  },

  async deleteGeofenceCategory(geofenceCategoryId: string): Promise<{ affected: number }> {
    const data: DeleteResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.deleteGeofenceCategory,
      { geofenceCategoryId },
    );
    return data.deleteGeofenceCategory;
  },

  // ===== Geofences =====

  async getGeofences(filter?: GeofenceFilterInput): Promise<Geofence[]> {
    const data: GetGeofencesResponse = await graphQlClient.request(
      GeofenceGraphQL.queries.getGeofences,
      { filter: filter ?? null },
    );
    return data.getGeofences || [];
  },

  async getGeofence(geofenceId: string): Promise<Geofence> {
    if (!geofenceId) throw new Error('geofenceId is required');
    const data: GetGeofenceResponse = await graphQlClient.request(
      GeofenceGraphQL.queries.getGeofence,
      { geofenceId },
    );
    return data.getGeofence;
  },

  async createGeofence(input: GeofenceCreateInput): Promise<Geofence> {
    const data: CreateGeofenceResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.createGeofence,
      { input },
    );
    return data.createGeofence;
  },

  async updateGeofence(input: GeofenceUpdateInput): Promise<Geofence> {
    const data: UpdateGeofenceResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.updateGeofence,
      { input },
    );
    return data.updateGeofence;
  },

  async setGeofenceActive(geofenceId: string, isActive: boolean): Promise<Geofence> {
    const data: SetGeofenceActiveResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.setGeofenceActive,
      { geofenceId, isActive },
    );
    return data.setGeofenceActive;
  },

  async deleteGeofence(geofenceId: string): Promise<{ affected: number }> {
    const data: DeleteResponse = await graphQlClient.request(
      GeofenceGraphQL.mutations.deleteGeofence,
      { geofenceId },
    );
    return data.deleteGeofence;
  },
};

export default geofenceService;
